/**
 * Centralized error logging utility for the frontend
 * Provides structured logging with user context and error tracking
 */

class ErrorLogger {
  constructor() {
    this.apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    this.environment = process.env.NODE_ENV || 'development';
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  getUserContext() {
    try {
      const user = localStorage.getItem('syllabus-app.user');
      const userName = localStorage.getItem('syllabus-app.user_name');
      return {
        user: user || 'anonymous',
        userName: userName || 'unknown',
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      };
    } catch (e) {
      return {
        user: 'anonymous',
        userName: 'unknown',
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        userAgent: 'unknown',
        url: window.location.href,
        pathname: window.location.pathname || 'unknown',
        search: window.location.search || '',
        hash: window.location.hash || '',
        referrer: document.referrer || 'unknown'
      };
    }
  }

  /**
   * Log error with context information
   * @param {Error|string} error - The error to log
   * @param {string} context - Context where error occurred (e.g., 'upload', 'auth', 'api')
   * @param {Object} additionalData - Additional data to include
   */
  logError(error, context = 'general', additionalData = {}) {
    const errorData = {
      level: 'error',
      context,
      message: error.message || error,
      stack: error.stack,
      code: error.code,
      ...this.getUserContext(),
      ...additionalData
    };

    // Log to console for development
    console.error(`[${context.toUpperCase()}] Error:`, errorData);

    // Send to backend for production logging (non-blocking)
    if (this.environment === 'production') {
      this.sendToBackend(errorData).catch(e => 
        console.warn('Failed to send error to backend:', e)
      );
    }

    return errorData;
  }

  /**
   * Log warning with context
   */
  logWarning(message, context = 'general', additionalData = {}) {
    const warningData = {
      level: 'warning',
      context,
      message,
      ...this.getUserContext(),
      ...additionalData
    };

    console.warn(`[${context.toUpperCase()}] Warning:`, warningData);

    if (this.environment === 'production') {
      this.sendToBackend(warningData).catch(e => 
        console.warn('Failed to send warning to backend:', e)
      );
    }

    return warningData;
  }

  /**
   * Log info with context
   */
  logInfo(message, context = 'general', additionalData = {}) {
    const infoData = {
      level: 'info',
      context,
      message,
      ...this.getUserContext(),
      ...additionalData
    };

    console.log(`[${context.toUpperCase()}] Info:`, infoData);

    return infoData;
  }

  /**
   * Log API request/response for debugging
   */
  logApiCall(method, url, requestData, responseData, duration, error = null) {
    const apiData = {
      level: error ? 'error' : 'info',
      context: 'api',
      method,
      url,
      requestData: this.sanitizeData(requestData),
      responseData: this.sanitizeData(responseData),
      duration,
      error: error ? {
        message: error.message,
        code: error.code,
        status: error.response?.status
      } : null,
      ...this.getUserContext()
    };

    if (error) {
      console.error('[API] Request failed:', apiData);
    } else {
      console.log('[API] Request completed:', apiData);
    }

    if (this.environment === 'production' && error) {
      this.sendToBackend(apiData).catch(e => 
        console.warn('Failed to send API error to backend:', e)
      );
    }

    return apiData;
  }

  /**
   * Remove sensitive data from logs
   */
  sanitizeData(data) {
    if (!data) return data;
    
    const sensitiveKeys = ['password', 'token', 'authorization', 'cookie'];
    const sanitized = JSON.parse(JSON.stringify(data));
    
    const sanitizeObject = (obj) => {
      if (typeof obj !== 'object' || obj === null) return obj;
      
      Object.keys(obj).forEach(key => {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
          obj[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          sanitizeObject(obj[key]);
        }
      });
    };
    
    sanitizeObject(sanitized);
    return sanitized;
  }

  /**
   * Send log data to backend (non-blocking)
   */
  async sendToBackend(logData) {
    try {
      // Only send errors and warnings to backend to avoid spam
      if (!['error', 'warning'].includes(logData.level)) {
        return;
      }

      const response = await fetch(`${this.apiEndpoint}/log-frontend-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (e) {
      // Silently fail - don't want logging to break the app
      console.warn('Backend logging failed:', e.message);
    }
  }

  /**
   * Track user actions for debugging
   */
  logUserAction(action, details = {}) {
    const actionData = {
      level: 'info',
      context: 'user-action',
      action,
      details,
      ...this.getUserContext()
    };

    console.log('[USER-ACTION]', actionData);
    return actionData;
  }

  /**
   * Sanitize URL for logging (remove sensitive parameters)
   */
  sanitizeUrl(url) {
    if (!url) return 'unknown';
    
    try {
      const urlObj = new URL(url);
      // Remove potentially sensitive query parameters
      const sensitiveParams = ['token', 'key', 'password', 'secret', 'auth'];
      
      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });
      
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, just return the original (truncated if too long)
      return url.length > 200 ? url.substring(0, 200) + '...' : url;
    }
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger;